import { Link } from 'react-router-dom';
import { Settings, ArrowRight, Bell, CheckCircle, TrendingUp, Briefcase, Database, Workflow } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useState } from 'react';
import { toast } from 'sonner';
import { createLead } from '@/lib/api';

const businessPrograms = [
  {
    name: 'Salesforce',
    fullName: 'Salesforce CRM',
    description: 'Become a certified Salesforce professional and master the world\'s #1 CRM platform.',
    certifications: ['Salesforce Administrator', 'Salesforce Developer', 'Salesforce Consultant'],
    color: 'from-blue-500 to-cyan-600'
  },
  {
    name: 'ServiceNow',
    fullName: 'ServiceNow',
    description: 'Master IT service management and digital workflows with ServiceNow.',
    certifications: ['ServiceNow Administrator', 'ServiceNow Developer', 'ITSM Implementation'],
    color: 'from-green-500 to-green-700'
  }
];

const skillTracks = [
  {
    icon: Database,
    title: 'Salesforce mastery',
    skills: ['Lightning Experience & Flows', 'Apex & LWC development', 'Sales/Service Cloud configuration', 'Data modeling & security'],
  },
  {
    icon: Workflow,
    title: 'ServiceNow ITSM',
    skills: ['Incident, problem & change management', 'CMDB & Discovery', 'Flow Designer & integrations', 'Performance Analytics dashboards'],
  },
];

const careerOutcomes = [
  { role: 'Salesforce Administrator', salary: '$80K–$120K', growth: '+11% YoY' },
  { role: 'ServiceNow Developer', salary: '$110K–$155K', growth: '+19% YoY' },
  { role: 'Senior Implementation Consultant', salary: '$130K–$170K', growth: '+14% YoY' },
];

export default function BusinessSoftwarePage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleNotify = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await createLead({
        first_name: 'Interested',
        last_name: 'User',
        email,
        message: 'Interested in Business Software training programs (Salesforce/ServiceNow)',
        source: 'business_software_waitlist'
      });
      toast.success('You\'re on the list! We\'ll notify you when programs launch.');
      setEmail('');
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24" data-testid="business-software-page">
      <section className="py-16 lg:py-24 bg-gradient-to-br from-purple-50 via-white to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium mb-6">
              Coming Soon
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
              Business Software Training
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              Master leading CRM and service management platforms with our upcoming instructor-led training programs. 
              Get certified in Salesforce and ServiceNow with hands-on labs and expert guidance.
            </p>
            
            <form onSubmit={handleNotify} className="flex flex-col sm:flex-row gap-3 max-w-md">
              <Input 
                type="email" 
                placeholder="Enter your email" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1"
                data-testid="business-notify-email"
              />
              <Button type="submit" className="bg-purple-600 hover:bg-purple-700 text-white rounded-full px-6" disabled={loading}>
                <Bell className="w-4 h-4 mr-2" />
                {loading ? 'Subscribing...' : 'Notify Me'}
              </Button>
            </form>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">What you'll master</h2>
            <p className="text-slate-600">Two of the most in-demand business platforms in enterprise IT — taught from configuration to advanced development.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {skillTracks.map((track) => (
              <Card key={track.title} className="border-slate-200">
                <CardContent className="p-7">
                  <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center mb-4">
                    <track.icon className="w-6 h-6 text-purple-700" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{track.title}</h3>
                  <ul className="space-y-2">
                    {track.skills.map((s) => (
                      <li key={s} className="flex items-start text-sm text-slate-700">
                        <CheckCircle className="w-4 h-4 text-purple-600 mr-2 mt-0.5 flex-shrink-0" /> {s}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 gap-3">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-3">Where this takes your career</h2>
              <p className="text-slate-600 max-w-2xl">Salesforce and ServiceNow consultants are among the highest-paid non-engineering tracks in enterprise tech.</p>
            </div>
            <span className="inline-flex items-center text-sm text-slate-500"><TrendingUp className="w-4 h-4 mr-1 text-green-600" /> US BLS / industry reports, 2025–2026</span>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {careerOutcomes.map((c) => (
              <Card key={c.role} className="bg-white">
                <CardContent className="p-6">
                  <Briefcase className="w-6 h-6 text-purple-600 mb-3" />
                  <div className="text-lg font-bold text-slate-900">{c.role}</div>
                  <div className="text-2xl font-extrabold text-slate-900 mt-2">{c.salary}</div>
                  <div className="text-sm text-green-700 font-medium mt-1">{c.growth}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-8">Upcoming Programs</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {businessPrograms.map((program) => (
              <Card key={program.name} className="border-2 border-dashed border-slate-200 bg-slate-50/50">
                <CardContent className="p-8">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${program.color} flex items-center justify-center mb-4`}>
                    <Settings className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{program.fullName}</h3>
                  <p className="text-slate-600 mb-4">{program.description}</p>
                  
                  <div className="mb-4">
                    <p className="text-sm font-medium text-slate-500 mb-2">Certification Tracks</p>
                    <ul className="space-y-1">
                      {program.certifications.map((cert) => (
                        <li key={cert} className="flex items-center text-sm text-slate-600">
                          <CheckCircle className="w-4 h-4 text-purple-500 mr-2" />
                          {cert}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <span className="inline-block px-3 py-1 bg-slate-200 text-slate-600 rounded-full text-sm">
                    Launching Q2 2026
                  </span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Want ERP Training Now?</h2>
          <p className="text-slate-400 mb-8">Our ERP programs are available today with live instruction.</p>
          <Link to="/programs">
            <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8">
              View ERP Programs <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
